import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { TimedRequestCache } from 'src/app/services/timed-request-cache';
import { DemandApprovalSummary } from 'src/assets/types/interface/demand.interface';
import { OsProviderDatesResponseDto } from 'src/assets/types/interface/os-provider-dates-response.interface';
import { ServiceOrdersResponse } from 'src/assets/types/interface/service-order-response.interface';
import {
  ServiceOrder,
  ServiceOrderApprovalSummary,
  ServiceOrderListItem,
} from 'src/assets/types/interface/service-order.interface';
import { Status } from 'src/assets/types/interface/status.interface';
import { environment } from 'src/environments/environment';

export interface ServiceOrderStatusGroupItem {
  uuid: string;
  name: string;
  displayName: string;
  flow: 'all' | 'self-managed' | 'moderated';
}

export interface ServiceOrderStatusGroupTab {
  key: 'acao' | 'acompanhamento' | 'historico';
  label: string;
  statuses: ServiceOrderStatusGroupItem[];
}

export interface GroupedServiceOrderResponseItem<TItem = ServiceOrderListItem> {
  key: string;
  label: string;
  statuses?: string[];
  total: number;
  page: number;
  pageSize: number;
  service_orders: TItem[];
}

export interface GroupedServiceOrdersResponse<TItem = ServiceOrderListItem> {
  tenantType: 'Tomador' | 'Prestador' | null;
  profileName: string | null;
  group: GroupedServiceOrderResponseItem<TItem> | null;
}

export interface GroupedServiceOrderRequestItem {
  key: string;
  page: number;
  take: number;
}

export interface GroupedServiceOrdersTabResponse<TItem = ServiceOrderListItem> {
  tenantType: 'Tomador' | 'Prestador' | null;
  profileName: string | null;
  tab: ServiceOrderStatusGroupTab | null;
  groups: GroupedServiceOrderResponseItem<TItem>[];
}

export type ApprovalHistoryGroupKey = 'demands' | 'budgets' | 'service-orders';

export interface ApprovalHistoryBudgetItem {
  uuid: string;
  version: number;
  amount: number | null;
  statusName: string | null;
  decisionReason: string | null;
  proposalUuid: string | null;
  serviceOrderUuid: string | null;
  createdAt: string | Date | null;
  updatedAt: string | Date | null;
}

export interface ApprovalHistoryFeedItem {
  entityType: 'service-order' | 'demand' | 'budget';
  decidedAt: string | null;
  serviceOrder: ServiceOrderApprovalSummary | null;
  demand: DemandApprovalSummary | null;
  budget?: ApprovalHistoryBudgetItem | null;
}

export interface ApprovalHistoryFeedResponse {
  tenantType: 'Tomador' | 'Prestador' | null;
  profileName: string | null;
  history: {
    key?: ApprovalHistoryGroupKey;
    label?: string;
    total: number;
    page: number;
    pageSize: number;
    items: ApprovalHistoryFeedItem[];
  } | null;
}

export interface SidebarCountersResponse {
  tenantType: 'Tomador' | 'Prestador' | null;
  profileName: string | null;
  version: number;
  generatedAt: string;
  programs: {
    serviceOrders: {
      actionPendingTotal: number;
    };
    approvals: {
      total: number;
    };
  };
}

export enum EstimatedDateType {
  BUDGET_DELIVERY = 'ESTIMATED_BUDGET_DELIVERY',
  EXECUTION_START = 'ESTIMATED_EXECUTION_START',
  EXECUTION_END = 'ESTIMATED_EXECUTION_END',
}

export enum ExecutionDateType {
  EXECUTION_START = 'EXECUTION_START',
  EXECUTION_END = 'EXECUTION_END',
}

@Injectable({
  providedIn: 'root',
})
export class ServiceOrderService {
  private readonly apiUrl = environment.WO_API_URL;
  private readonly serviceOrderDetailRequests =
    new TimedRequestCache<ServiceOrder>();
  private readonly providerServiceOrderDetailRequests =
    new TimedRequestCache<ServiceOrder>();

  constructor(private http: HttpClient) {}

  getServiceOrdersProvider(
    page: number,
    take: number,
    filters?: {
      userName?: string;
      companyName?: string;
      date?: string;
      number?: string;
      statusName?: string[];
    },
  ): Observable<ServiceOrdersResponse> {
    const access_token = localStorage.getItem('access_token') ?? '';

    let params = new HttpParams()
      .set('page', String(page))
      .set('take', String(take));

    if (filters?.userName) {
      params = params.set('user', filters.userName);
    }

    if (filters?.companyName) {
      params = params.set('company', filters.companyName);
    }

    if (filters?.date) {
      const formattedDate = new Date(filters.date).toISOString().split('T')[0];
      params = params.set('date', formattedDate);
    }

    if (filters?.number) {
      params = params.set('number', filters.number);
    }

    if (filters?.statusName?.length) {
      const statusParam = filters.statusName
        ?.filter((s) => s.trim() !== '')
        .join(',');
      if (statusParam) {
        params = params.set('status', statusParam);
      }
    }

    const url = `${this.apiUrl}/service-order/provider`;

    return this.http.get<ServiceOrdersResponse>(url, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
      params,
    });
  }

  getServiceOrderProviderStatus(): Observable<Status[]> {
    const url = `${this.apiUrl}/service-order/provider/statuses`;
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<Status[]>(url, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
  }

  getServiceOrderStatus(): Observable<Status[]> {
    const url = `${this.apiUrl}/service-order/statuses`;
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<Status[]>(url, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
  }

  getSidebarCounters(): Observable<SidebarCountersResponse> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<SidebarCountersResponse>(
      `${this.apiUrl}/sidebar-counters`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getGroupedTabGroups(
    tabKey: 'acao' | 'acompanhamento' | 'historico',
    groups: GroupedServiceOrderRequestItem[],
    filters?: {
      search?: string;
      statusDisplayNames?: string[];
      source?: 'AUTO_GESTAO' | 'GERENCIADA' | 'MODERACAO';
      dateFrom?: string;
      dateTo?: string;
      timeZone?: string;
    },
  ): Observable<GroupedServiceOrdersTabResponse> {
    const access_token = localStorage.getItem('access_token') ?? '';

    return this.http.post<GroupedServiceOrdersTabResponse>(
      `${this.apiUrl}/service-order/queries/tab-groups`,
      {
        tabKey,
        filters: {
          search: filters?.search?.trim() || undefined,
          status: filters?.statusDisplayNames?.length
            ? filters.statusDisplayNames
                .filter((status) => status.trim() !== '')
                .join(',')
            : undefined,
          source: filters?.source,
          dateFrom: filters?.dateFrom,
          dateTo: filters?.dateTo,
          timeZone: filters?.timeZone,
        },
        groups,
      },
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getApprovalHistoryGroup(
    groupKey: ApprovalHistoryGroupKey,
    page: number,
    take: number,
    filters?: {
      search?: string;
      statusDisplayNames?: string[];
      dateFrom?: string;
      dateTo?: string;
    },
  ): Observable<ApprovalHistoryFeedResponse> {
    const access_token = localStorage.getItem('access_token') ?? '';
    let params = new HttpParams();

    if (filters?.search?.trim()) {
      params = params.set('search', filters.search.trim());
    }

    if (filters?.statusDisplayNames?.length) {
      const statusParam = filters.statusDisplayNames
        .filter((status) => status.trim() !== '')
        .join(',');

      if (statusParam) {
        params = params.set('status', statusParam);
      }
    }

    if (filters?.dateFrom?.trim()) {
      params = params.set('dateFrom', filters.dateFrom.trim());
    }

    if (filters?.dateTo?.trim()) {
      params = params.set('dateTo', filters.dateTo.trim());
    }

    params = params.set('page', String(page)).set('take', String(take));

    return this.http.get<ApprovalHistoryFeedResponse>(
      `${this.apiUrl}/service-order/approval-history-groups/${groupKey}`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
        params,
      },
    );
  }

  ruleIsAlreadyConfigured(): Observable<any> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<any>(
      `${this.apiUrl}/service-order/provider/eligibility`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  findServiceOrder(serviceOrderUuid: string): Observable<ServiceOrder> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.serviceOrderDetailRequests.get(
      `${access_token}:${serviceOrderUuid}`,
      () =>
        this.http.get<ServiceOrder>(
          `${this.apiUrl}/service-order/${serviceOrderUuid}`,
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          },
        ),
    );
  }

  refreshServiceOrder(serviceOrderUuid: string): Observable<ServiceOrder> {
    const access_token = localStorage.getItem('access_token') ?? '';
    this.serviceOrderDetailRequests.invalidate(
      `${access_token}:${serviceOrderUuid}`,
    );

    return this.findServiceOrder(serviceOrderUuid);
  }

  findProviderServiceOrder(serviceOrderUuid: string): Observable<ServiceOrder> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.providerServiceOrderDetailRequests.get(
      `${access_token}:${serviceOrderUuid}`,
      () =>
        this.http.get<ServiceOrder>(
          `${this.apiUrl}/service-order/provider/${serviceOrderUuid}`,
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          },
        ),
    );
  }

  refreshProviderServiceOrder(
    serviceOrderUuid: string,
  ): Observable<ServiceOrder> {
    const access_token = localStorage.getItem('access_token') ?? '';
    this.providerServiceOrderDetailRequests.invalidate(
      `${access_token}:${serviceOrderUuid}`,
    );

    return this.findProviderServiceOrder(serviceOrderUuid);
  }

  acceptServiceOrder(serviceOrderUuid: string): Observable<any> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.patch(
        `${this.apiUrl}/service-order/provider/${serviceOrderUuid}/accept`,
        {},
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          observe: 'response',
        },
      ),
    );
  }

  refuseServiceOrder(serviceOrderUuid: string): Observable<any> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.patch(
        `${this.apiUrl}/service-order/provider/${serviceOrderUuid}/refuse`,
        {},
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          observe: 'response',
        },
      ),
    );
  }

  allocateOwnProvider(
    serviceOrderUuid: string,
    providerUuid: string,
  ): Observable<any> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.post(
        `${this.apiUrl}/service-order/${serviceOrderUuid}/providers`,
        { providerUuid },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  registerExecutionCompletion(
    serviceOrderUuid: string,
    data: {
      executionStartDate: Date;
      executionEndDate: Date;
      executionDescription: string;
      evidenceUrls?: string[];
    },
  ) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.patch(
        `${this.apiUrl}/service-order/${serviceOrderUuid}/execution-completion`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          observe: 'response',
        },
      ),
    );
  }

  validateExecutionCompletion(
    serviceOrderUuid: string,
    data: { details?: string } = {},
  ) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.patch(
        `${this.apiUrl}/service-order/${serviceOrderUuid}/completion/validate`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          observe: 'response',
        },
      ),
    );
  }

  approverRejectExecutionCompletion(
    serviceOrderUuid: string,
    data: {
      reason: string;
      action: 'REWORK' | 'CANCEL';
      osProviderUuid?: string;
      cancellationScope?: 'ALL_PROVIDERS' | 'OS_PROVIDER';
    },
  ) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.patch(
        `${this.apiUrl}/service-order/${serviceOrderUuid}/completion/reject`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          observe: 'response',
        },
      ),
    );
  }

  providerFinishServiceOrder(
    osProviderUuid: string,
    data: { rating: number | undefined; details: string },
  ) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.patch(
        `${this.apiUrl}/service-order/provider/${osProviderUuid}/execution-completion`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          observe: 'response',
        },
      ),
    );
  }

  takerCancelServiceOrder(serviceOrderUuid: string, data: { details: string }) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.patch(
        `${this.apiUrl}/service-order/${serviceOrderUuid}/cancel`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          observe: 'response',
        },
      ),
    );
  }

  setEstimatedExecutionDate(
    osProviderUuid: string,
    date: Date,
    type: EstimatedDateType,
  ): Observable<OsProviderDatesResponseDto> {
    const access_token = localStorage.getItem('access_token') ?? '';
    const data = {
      date,
      type,
    };
    return this.invalidateDetailCachesAfter(
      this.http.patch<OsProviderDatesResponseDto>(
        `${this.apiUrl}/service-order/provider/${osProviderUuid}/estimated-date`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  setTakerEstimatedExecutionPeriod(
    serviceOrderUuid: string,
    data: {
      estimatedExecutionStartDate: Date;
      estimatedExecutionEndDate: Date;
    },
  ): Observable<OsProviderDatesResponseDto> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.patch<OsProviderDatesResponseDto>(
        `${this.apiUrl}/service-order/${serviceOrderUuid}/estimated-execution-period`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  setExecutionDate(
    osProviderUuid: string,
    date: Date,
    type: ExecutionDateType,
  ): Observable<OsProviderDatesResponseDto> {
    const access_token = localStorage.getItem('access_token') ?? '';
    const data = {
      date,
      type,
    };
    return this.invalidateDetailCachesAfter(
      this.http.patch<OsProviderDatesResponseDto>(
        `${this.apiUrl}/service-order/provider/${osProviderUuid}/execution-date`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  private invalidateDetailCachesAfter<T>(
    request: Observable<T>,
  ): Observable<T> {
    return request.pipe(
      tap(() => {
        this.serviceOrderDetailRequests.clear();
        this.providerServiceOrderDetailRequests.clear();
      }),
    );
  }
}
